import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { freeze } from "immer";
import { getPageSetSurvey } from "src/pageDefinitions/actions/survey/util";
import { Direction } from "src/pageDefinitions/types";
import { trackEvent } from "src/utils/api/tracker";
import {
  addSurveyAnswers,
  SurveyAnswersState,
} from "src/utils/redux/slices/surveyAnswers";
import { trackViewContent } from "src/utils/services/ConversionTracker";
import { useCallbackRef } from "../lifecycle";
import { useGotoOptions, usePageSet, usePageSetEvents } from "../pageSet";
import { useCoreStore } from "../redux";
import { executeActions } from "@pageDefinitions/goto/pageSet";
import { prepareAndTrackSurveyAnswers } from "@utils/mainSurvey";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";

export type UseSurveyConfig<Q extends keyof SurveyAnswersState> = {
  /**
   * Custom validator. Used on init and when `canNowProceed` is
   * omitted from `addAnswers` called.
   *
   * Defaults to marking responses without explicit validation as invalid.
   */
  validate?: (
    answer: SurveyAnswersState[Q],
    answers: SurveyAnswersState
  ) => boolean;
};

export function useSurvey<Q extends keyof SurveyAnswersState>(
  questionId: Q,
  { validate = () => false }: UseSurveyConfig<Q> = {}
) {
  const store = useCoreStore();
  const gotoOptions = useGotoOptions();

  const { activePageSet, activePage } = usePageSet();

  // Render invalidator. Ensures that we update when answers are
  // provided.
  const [, setAnswersUpdate] = useState(Date.now());

  const validateRef = useCallbackRef(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return validate(currentAnswers[questionId], currentAnswers);
  });

  const [canProceed, setCanProceed] = useState(validateRef());
  const [isSending, setIsSending] = useState(false);

  const surveyBlock = getPageSetSurvey(activePageSet, activePage);
  const surveyName = activePage ? `On${surveyBlock.name}` : "OnUnknownSurvey";

  const questionSetRef = useRef(null);

  useEffect(() => {
    // We want to only reset the proceed status if it has not been previously set
    // for the current question. This can happen if `addAnswers` is called
    // prior to this effect running.
    if (questionId !== questionSetRef.current) {
      setCanProceed(validateRef());
    }

    trackEvent(surveyName, { questionId });
    trackViewContent();
  }, [questionId, surveyName, validateRef]);

  usePageSetEvents(async (event, params) => {
    if (event === "before:goto") {
      setIsSending(false);

      if (params.direction === Direction.BACKWARD) {
        trackEvent(`${surveyName}-BackBtnClicked`, {
          clickedOnQuestionId: questionId,
        });
      } else {
        const currentSurvey = getPageSetSurvey(activePageSet, activePage);
        // Tracking event and saving for each question in all surveys
        prepareAndTrackSurveyAnswers(
          questionId,
          store.getState().surveyAnswers || {}
        );
        await executeActions(
          activePageSet,
          activePage,
          currentSurvey?.onAnswered,
          gotoOptions,
          { questionId }
        );
      }
    }
  });

  // Returns if the user can proceed, given their current answer selection.
  const addAnswers = useCallback(
    (answers?: SurveyAnswersState, canNowProceed?: boolean) => {
      if (answers) {
        store.dispatch(addSurveyAnswers(answers));
      }
      questionSetRef.current = questionId;
      updateCurrentAnswers(answers);
      setAnswersUpdate(Date.now());

      const newCanProceed =
        canNowProceed == null ? validateRef() : canNowProceed;
      setCanProceed(newCanProceed);
      return newCanProceed;
    },
    [questionId, store, validateRef]
  );

  /**
   * Helper for when the survey question is a single input, where the answer should equal
   * that input's value.
   */
  const handleChanged = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const answers: SurveyAnswersState = {
        [questionId]: e.target.value,
      };

      addAnswers(answers);
    },
    [questionId, addAnswers]
  );

  const sendAnswers = useCallback(() => {
    setIsSending(true);
  }, []);

  return {
    questionId,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    currentAnswers,

    canProceed,
    isSending,

    addAnswers,
    handleChanged,
    sendAnswers,
  };
}

// Persistent storage key for survey answers
// This allows us to retain questions on refresh, while letting us isolate
// the current survey data from SurveyAnswersState.
//
// This is intentionally inlined to export private API to hook.
const STORAGE_KEY = "currentSurveyAnswers";
let currentAnswers: SurveyAnswersState =
  getNoomSessionStorage(STORAGE_KEY) || {};

function updateCurrentAnswers(answers: SurveyAnswersState) {
  const changed =
    !answers ||
    !!Object.keys(answers).find((key) => currentAnswers[key] !== answers[key]);

  // Only update if values have changed. This prevents infinite update loops
  // from identity updates.
  if (changed) {
    currentAnswers = {
      ...currentAnswers,
      ...answers,
    };
    setNoomSessionStorage(STORAGE_KEY, currentAnswers);
  }
}

export function getCurrentSurveyAnswers() {
  return freeze(currentAnswers, true);
}
export function resetCurrentSurvey(newState: SurveyAnswersState = {}) {
  currentAnswers = {};
  updateCurrentAnswers(newState);
}
